import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Info, OctagonX, TriangleAlert } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"
import { useUILabels } from "./ui-labels"

const inlineConfirmVariants = cva(
  "relative isolate flex w-full items-center gap-2.5 rounded-md p-1.5 ring-1 ring-inset min-h-10 " +
  "animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out",
  {
    variants: {
      variant: {
        info: "bg-primary/[0.07] ring-primary/20",
        warning: "bg-warning/[0.08] ring-warning/25",
        danger: "bg-destructive/[0.08] ring-destructive/30",
      },
    },
    defaultVariants: { variant: "warning" },
  }
)

const accentBarVariants = cva(
  "pointer-events-none absolute inset-y-1.5 start-0 w-[2px] rounded-full",
  {
    variants: {
      variant: {
        info: "bg-primary",
        warning: "bg-warning",
        danger: "bg-destructive",
      },
    },
    defaultVariants: { variant: "warning" },
  }
)

const iconVariants = cva("h-4 w-4 shrink-0 ms-0.5", {
  variants: {
    variant: {
      info: "text-primary",
      warning: "text-warning",
      danger: "text-destructive",
    },
  },
  defaultVariants: { variant: "warning" },
})

const confirmButtonVariants = cva(
  "h-7 px-3 bg-transparent",
  {
    variants: {
      variant: {
        info: "border-primary/40 text-primary hover:bg-primary/10 hover:text-primary",
        warning: "border-warning/45 text-warning hover:bg-warning/10 hover:text-warning",
        danger: "border-destructive/45 text-destructive hover:bg-destructive/10 hover:text-destructive",
      },
    },
    defaultVariants: { variant: "warning" },
  }
)

const VARIANT_ICON: Record<NonNullable<VariantProps<typeof inlineConfirmVariants>["variant"]>, React.ComponentType<{ className?: string }>> = {
  info: Info,
  warning: TriangleAlert,
  danger: OctagonX,
}

type LabelPreset = "yesNo" | "confirmCancel" | "okCancel"

type InlineConfirmCommonProps = {
  variant?: VariantProps<typeof inlineConfirmVariants>["variant"]
  message: React.ReactNode
  onConfirm: () => void
  onCancel: () => void
  confirmDisabled?: boolean
  className?: string
}

type InlineConfirmProps =
  | (InlineConfirmCommonProps & {
      labels?: LabelPreset
      confirmLabel?: never
      cancelLabel?: never
    })
  | (InlineConfirmCommonProps & {
      labels: "custom"
      confirmLabel: string
      cancelLabel: string
    })

export const InlineConfirm = React.forwardRef<HTMLDivElement, InlineConfirmProps>(
  (props, ref) => {
    const {
      variant = "warning",
      message,
      onConfirm,
      onCancel,
      confirmDisabled,
      className,
    } = props
    const labels = props.labels ?? "confirmCancel"
    const labelMap = useUILabels().inlineConfirm

    const resolved =
      labels === "custom"
        ? { confirm: props.confirmLabel, cancel: props.cancelLabel }
        : labelMap[labels]

    const Icon = VARIANT_ICON[variant ?? "warning"]

    return (
      <div
        ref={ref}
        role="alertdialog"
        aria-live="polite"
        className={cn(inlineConfirmVariants({ variant }), className)}
      >
        <span aria-hidden className={accentBarVariants({ variant })} />
        <Icon className={iconVariants({ variant })} />
        <div className="flex-1 min-w-0 text-sm text-foreground/90 leading-snug">
          {message}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-3 text-muted-foreground hover:text-foreground"
            onClick={onCancel}
          >
            {resolved.cancel}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(confirmButtonVariants({ variant }))}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {resolved.confirm}
          </Button>
        </div>
      </div>
    )
  }
)
InlineConfirm.displayName = "InlineConfirm"
